/**
 * rentCap.js — PURE rent-cap maths for Ireland's national rent control regime.
 *
 * ── Why this module exists ─────────────────────────────────────────────
 * The public lead-magnet calculator at /tools/rent-calculator must render
 * for logged-out visitors from a Google Ads click. Everything it imports
 * is therefore pulled into the initial chunk, so this file MUST stay free
 * of Firestore/auth/analytics imports (src/utils/analytics.js imports
 * ../firebase — do not import it here). Keep this file dependency-free:
 * no React, no MUI, no Firebase. Only plain JS.
 *
 * src/pages/v2/RentReview.js (the authed product page) and
 * src/pages/v2/RentCalculator.js (the public page) both import from here
 * so the two surfaces can never drift apart on the numbers.
 *
 * ── The law encoded here ───────────────────────────────────────────────
 * Residential Tenancies (Miscellaneous Provisions) Act 2026, in force
 * 1 March 2026. It ABOLISHED the Rent Pressure Zone system and replaced
 * it with a NATIONWIDE rent cap. (RPZs had themselves been extended to
 * the whole country on 20 June 2025; the RPZ regime was dismantled on
 * 28 February 2026.) So "am I in an RPZ?" is no longer the question it
 * was — every private tenancy in the State is now rent-capped.
 *
 * Rules encoded, with sources (all checked 2026-09-16):
 *
 * 1. CAP: rent may rise by the LOWER of 2% per annum or the rate of
 *    inflation measured by the Consumer Price Index (CPI).
 *    NOTE: it is CPI from 1 March 2026 — the pre-2026 RPZ regime used
 *    HICP. Callers passing an inflation figure must pass CPI.
 *    https://rtb.ie/renting/setting-and-reviewing-private-rents-from-1-march-2026/
 *    https://rtb.ie/renting/guide-to-rent-review-notices/
 *    https://www.matheson.com/residential-tenancies-legislation-2026/
 *
 * 2. NEW APARTMENTS: a development whose commencement notice was lodged
 *    on or after 10 June 2025 is NOT subject to the 2% ceiling — such
 *    rents may rise in line with CPI only (which can exceed 2%).
 *    https://rtb.ie/renting/guide-to-rent-review-notices/
 *
 * 3. INTERVAL: one review every 12 months. TRANSITIONAL EXCEPTION — in
 *    areas that became an RPZ within the previous 2 years, rent cannot
 *    be reviewed until 24 months after the rent was last set/reviewed.
 *    https://rtb.ie/renting/setting-and-reviewing-private-rents-from-1-march-2026/
 *
 * 4. NOTICE: at least 90 days' written notice before the new rent takes
 *    effect, on the RTB's prescribed Notice of Rent Review form, AND a
 *    copy served on the RTB within 7 days of serving the tenant — the
 *    notice is invalid without it. (This 7-day window replaced an
 *    earlier same-day requirement; rtbCompliance.js still carries the
 *    old same-day constant for termination notices, which is a
 *    different instrument.)
 *    https://rtb.ie/renting/guide-to-rent-review-notices/
 *
 * 5. PRO-RATA: the cap is expressed per annum and is applied to the
 *    actual number of months elapsed since the rent was last set. A
 *    24-month gap does not mean 2% — see maxIncreasePercent() for the
 *    treatment and its caveat.
 *
 * 6. MARKET RESET: for tenancies created on/after 1 March 2026, rent may
 *    be reset to market rate at the end of a 6-year Tenancy of Minimum
 *    Duration (3 years for student-specific accommodation). Tenancies
 *    created BEFORE 1 March 2026 cannot be reset to market rent while
 *    the tenancy is ongoing. This module reports the distinction; it
 *    does not compute market rent (no data source for that).
 *    https://rtb.ie/renting/setting-and-reviewing-private-rents-from-1-march-2026/
 *
 * UNCERTAIN / DELIBERATELY NOT GUESSED: the RTB does not publish the
 * exact statutory arithmetic its own calculator uses (its page defers to
 * "the Residential Tenancies Act 2004, as amended"). In particular
 * whether multi-year pro-rating is linear or compounded, and which
 * month's CPI print is pinned to each date, are not stated publicly.
 * This module uses SIMPLE (linear, non-compounded) pro-rating, which is
 * the conservative reading, and every consumer of it must tell the user
 * to confirm against the official RTB calculator before serving notice.
 */

// ── Constants ─────────────────────────────────────────────────────────

/** Annual ceiling, in percent, for tenancies that are not CPI-only. */
export const ANNUAL_CAP_PERCENT = 2;

/** Minimum months between rent reviews (standard case). */
export const REVIEW_INTERVAL_MONTHS = 12;

/**
 * Minimum months between reviews where the property is in an area that
 * became an RPZ within the 2 years before the regime changed.
 */
export const RECENT_RPZ_INTERVAL_MONTHS = 24;

/** Minimum days' notice before a new rent may take effect. */
export const NOTICE_DAYS = 90;

/** Days within which a copy of the notice must reach the RTB. */
export const RTB_COPY_DAYS = 7;

/** Date the national rent control regime commenced. */
export const REGIME_START = '2026-03-01';

/**
 * Commencement-notice date from which a new apartment development
 * escapes the 2% ceiling and is capped by CPI alone.
 */
export const NEW_BUILD_COMMENCEMENT_FROM = '2025-06-10';

/** Length of a Tenancy of Minimum Duration, in years. */
export const TENANCY_CYCLE_YEARS = 6;

/**
 * Default CPI figure: annual change in the Consumer Price Index in the
 * 12 months to August 2026, released by the CSO on 10 September 2026.
 * Checked 2026-09-16: 3.7%.
 * https://www.cso.ie/en/releasesandpublications/ep/p-cpi/consumerpriceindexaugust2026/
 *
 * At 3.7% CPI exceeds the 2% ceiling, so the 2% arm binds for ordinary
 * tenancies today; CPI only becomes the operative cap when it falls
 * below 2%, or for CPI-only new apartments. This is a DEFAULT the user
 * can edit — CPI moves monthly and this constant will go stale.
 */
export const DEFAULT_CPI_PERCENT = 3.7;
export const DEFAULT_CPI_PERIOD = '12 months to August 2026';
export const DEFAULT_CPI_SOURCE_URL =
  'https://www.cso.ie/en/releasesandpublications/ep/p-cpi/consumerpriceindexaugust2026/';

// ── Date helpers (timezone-safe) ──────────────────────────────────────
//
// Everything here works on 'YYYY-MM-DD' strings parsed as UTC midnight.
// Per CLAUDE.md, `new Date().toISOString().slice(0,10)` is a real bug in
// Dublin: after 23:00 BST it yields tomorrow, shifting every 12-month
// and 90-day boundary this calculator hangs on. todayISO() below reads
// the LOCAL calendar date instead.

/** Local (not UTC) calendar date as 'YYYY-MM-DD'. */
export function todayISO(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parse 'YYYY-MM-DD' to a UTC-midnight Date, or null if unparseable. */
export function parseISO(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const [, y, mo, d] = m;
  const date = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  // Reject impossible dates that JS would roll over (e.g. 2026-02-31).
  if (
    date.getUTCFullYear() !== Number(y)
    || date.getUTCMonth() !== Number(mo) - 1
    || date.getUTCDate() !== Number(d)
  ) return null;
  return date;
}

/** Format a Date or ISO string as a UTC-midnight 'YYYY-MM-DD'. */
export function toISO(date) {
  const d = parseISO(date);
  return d ? d.toISOString().slice(0, 10) : null;
}

/** Whole days from a to b (b - a). Null if either is unparseable. */
export function daysBetweenISO(a, b) {
  const da = parseISO(a);
  const db = parseISO(b);
  if (!da || !db) return null;
  return Math.round((db - da) / 86400000);
}

/** Add days to a date, returning a new Date. */
export function addDaysISO(date, days) {
  const d = parseISO(date);
  if (!d) return null;
  return new Date(d.getTime() + days * 86400000);
}

/**
 * Add whole months, clamping to the end of the target month.
 * Guards the `setMonth` end-of-month rollover called out in CLAUDE.md:
 * naive setMonth on 31 Aug + 6 months yields 3 March, not 28 February.
 */
export function addMonthsISO(date, months) {
  const d = parseISO(date);
  if (!d) return null;
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  const targetMonthLastDay = new Date(Date.UTC(y, m + months + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m + months, Math.min(day, targetMonthLastDay)));
}

/**
 * Elapsed months between two dates as a FRACTION, using completed months
 * plus the day-remainder over the length of the part-month. Pro-rating
 * needs a fractional figure, not a floor.
 */
export function monthsBetweenISO(a, b) {
  const da = parseISO(a);
  const db = parseISO(b);
  if (!da || !db) return null;
  if (db <= da) return 0;

  let whole = (db.getUTCFullYear() - da.getUTCFullYear()) * 12
    + (db.getUTCMonth() - da.getUTCMonth());
  if (db.getUTCDate() < da.getUTCDate()) whole -= 1;
  if (whole < 0) whole = 0;

  const anniversary = addMonthsISO(a, whole);
  const nextAnniversary = addMonthsISO(a, whole + 1);
  const spanDays = Math.round((nextAnniversary - anniversary) / 86400000);
  const extraDays = Math.round((db - anniversary) / 86400000);
  const fraction = spanDays > 0 ? extraDays / spanDays : 0;
  return whole + Math.max(0, Math.min(1, fraction));
}

/** Format an ISO date the Irish way: "16 September 2026". */
export function formatDateIE(date) {
  const d = parseISO(date);
  if (!d) return '';
  return d.toLocaleDateString('en-IE', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

/** Finite-number guard. `Number('') < 1` is false because NaN — see CLAUDE.md. */
function isPositiveFinite(n) {
  return Number.isFinite(n) && n > 0;
}

// ── Cap maths ─────────────────────────────────────────────────────────

/**
 * The per-annum cap that applies, and why.
 *
 * @param {Object} opts
 * @param {number} opts.cpiPercent        Annual CPI, in percent.
 * @param {boolean} opts.isNewBuildExempt Development commenced on/after
 *                                        10 June 2025 → CPI only, no 2% ceiling.
 * @returns {{ percent: number, basis: 'cpi'|'cap'|'equal', explanation: string }}
 */
export function applicableAnnualCapPercent({ cpiPercent, isNewBuildExempt = false }) {
  const cpi = Number(cpiPercent);
  const safeCpi = Number.isFinite(cpi) ? cpi : 0;

  if (isNewBuildExempt) {
    // CPI-only. A negative CPI still cannot mandate a decrease, so floor at 0.
    const pct = Math.max(0, safeCpi);
    return {
      percent: pct,
      basis: 'cpi',
      explanation:
        `This development's commencement notice is dated on or after 10 June 2025, so the 2% ceiling does not apply. `
        + `The cap is CPI alone: ${safeCpi}% per year.`,
    };
  }

  const pct = Math.max(0, Math.min(ANNUAL_CAP_PERCENT, safeCpi));
  let basis = 'equal';
  if (safeCpi < ANNUAL_CAP_PERCENT) basis = 'cpi';
  else if (safeCpi > ANNUAL_CAP_PERCENT) basis = 'cap';

  let explanation;
  if (basis === 'cpi') {
    explanation =
      `CPI (${safeCpi}%) is below the 2% ceiling, so CPI is the cap: ${pct}% per year.`;
  } else if (basis === 'cap') {
    explanation =
      `CPI is ${safeCpi}%, above the 2% ceiling, so the 2% ceiling is the cap: 2% per year.`;
  } else {
    explanation = 'CPI is exactly 2%, so the cap is 2% per year.';
  }
  return { percent: pct, basis, explanation };
}

/**
 * Pro-rate a per-annum cap across the actual elapsed months.
 *
 * SIMPLE (linear) pro-rating: annualPercent * months / 12. For a review
 * exactly 12 months on this is just the annual figure. For a 24-month
 * gap it yields 2x the annual percentage rather than compounding
 * (1.02^2 - 1 = 4.04%), i.e. 4.00% — marginally the more conservative
 * of the two readings. The RTB does not publish which it uses; see the
 * module header. Never silently rely on this for a notice you serve.
 */
export function proRatePercent(annualPercent, months) {
  if (!Number.isFinite(annualPercent) || !Number.isFinite(months)) return 0;
  if (months <= 0) return 0;
  return (annualPercent * months) / 12;
}

/** Round to cents without float drift. */
export function roundCents(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// ── Review timing ─────────────────────────────────────────────────────

/**
 * Earliest date a rent review may take effect, given when rent was last
 * set and whether the 24-month transitional rule applies.
 */
export function earliestReviewDate({ lastSetDate, recentRpzArea = false }) {
  const months = recentRpzArea ? RECENT_RPZ_INTERVAL_MONTHS : REVIEW_INTERVAL_MONTHS;
  const d = addMonthsISO(lastSetDate, months);
  return d ? toISO(d) : null;
}

/**
 * Full evaluation of a proposed rent review.
 *
 * @param {Object} input
 * @param {number|string} input.currentRent    Current monthly rent (EUR).
 * @param {string} input.lastSetDate           'YYYY-MM-DD' rent last set/reviewed.
 * @param {string} input.reviewDate            'YYYY-MM-DD' proposed effective date.
 * @param {string} [input.tenancyStartDate]    'YYYY-MM-DD' tenancy commenced.
 * @param {number} [input.cpiPercent]          Annual CPI %.
 * @param {boolean} [input.isNewBuildExempt]   Commencement notice >= 10 Jun 2025.
 * @param {boolean} [input.recentRpzArea]      Became an RPZ within prior 2 years.
 * @param {string} [input.today]               Override for "today" (testing).
 * @returns {Object} result — see shape below. `errors` non-empty means the
 *                   numeric outputs are null and the UI must not show a figure.
 */
export function calculateRentReview(input = {}) {
  const {
    currentRent,
    lastSetDate,
    reviewDate,
    tenancyStartDate = null,
    cpiPercent = DEFAULT_CPI_PERCENT,
    isNewBuildExempt = false,
    recentRpzArea = false,
    today = todayISO(),
  } = input;

  const errors = [];
  const warnings = [];

  const rent = typeof currentRent === 'string' ? Number(currentRent.trim()) : Number(currentRent);
  if (!isPositiveFinite(rent)) {
    errors.push('Enter the current monthly rent as a positive amount.');
  }

  const lastSet = toISO(lastSetDate);
  if (!lastSet) errors.push('Enter the date the rent was last set or reviewed.');

  const review = toISO(reviewDate);
  if (!review) errors.push('Enter the date you want the new rent to take effect.');

  if (lastSet && review && parseISO(review) <= parseISO(lastSet)) {
    errors.push('The review date must be after the date the rent was last set.');
  }

  if (errors.length) {
    return { ok: false, errors, warnings };
  }

  // ── Timing ──────────────────────────────────────────────────────────
  const intervalMonths = recentRpzArea ? RECENT_RPZ_INTERVAL_MONTHS : REVIEW_INTERVAL_MONTHS;
  const earliest = earliestReviewDate({ lastSetDate: lastSet, recentRpzArea });
  const reviewAllowed = parseISO(review) >= parseISO(earliest);
  if (!reviewAllowed) {
    warnings.push(
      `Rent can only be reviewed every ${intervalMonths} months`
      + `${recentRpzArea ? ' in an area that became a Rent Pressure Zone in the previous 2 years' : ''}. `
      + `The earliest this review can take effect is ${formatDateIE(earliest)}.`,
    );
  }

  // 90 days' notice, counted back from the effective date. If serving
  // today would not leave 90 clear days, the effective date has to move.
  const latestServeDate = toISO(addDaysISO(review, -NOTICE_DAYS));
  const noticeDaysAvailable = daysBetweenISO(today, review);
  const noticeIsPossible = noticeDaysAvailable !== null && noticeDaysAvailable >= NOTICE_DAYS;
  // Earliest effective date if the notice were served today.
  const effectiveIfServedToday = toISO(addDaysISO(today, NOTICE_DAYS));
  // The real earliest effective date honours BOTH the interval and the notice.
  const earliestEffectiveDate =
    parseISO(effectiveIfServedToday) > parseISO(earliest) ? effectiveIfServedToday : earliest;
  if (!noticeIsPossible) {
    warnings.push(
      `You must give at least ${NOTICE_DAYS} days' written notice. `
      + `A notice served today could not take effect until ${formatDateIE(effectiveIfServedToday)}.`,
    );
  }
  // QA #143: the dates panel must never show a serve-by date in the past or
  // an RTB-copy deadline after the rent takes effect. When the chosen
  // effective date is not achievable, the achievable plan is: serve today,
  // rent effective `effectiveIfServedToday`, copy to the RTB within 7 days
  // of serving. The RTB copy deadline always follows the SERVE date.
  const datesAchievable = noticeIsPossible && reviewAllowed;
  const serveDate = noticeIsPossible ? latestServeDate : today;
  const rtbCopyDeadline = toISO(addDaysISO(serveDate, RTB_COPY_DAYS));
  const recommendedEffectiveDate = datesAchievable ? review : earliestEffectiveDate;

  // ── Cap ─────────────────────────────────────────────────────────────
  const months = monthsBetweenISO(lastSet, review);
  const annual = applicableAnnualCapPercent({ cpiPercent, isNewBuildExempt });
  // Pro-rate only for periods beyond 12 months. A review at or under 12
  // months gets the full annual allowance (you cannot review sooner than
  // 12 months anyway, so a shorter period is already flagged above).
  const proRated = months > REVIEW_INTERVAL_MONTHS
    ? proRatePercent(annual.percent, months)
    : annual.percent;
  const increasePercent = Math.max(0, proRated);
  const increaseAmount = roundCents((rent * increasePercent) / 100);
  const maxRent = roundCents(rent + increaseAmount);

  // ── Tenancy-start branch (market reset) ─────────────────────────────
  const tenancyStart = toISO(tenancyStartDate);
  let tenancyRegime = null;
  if (tenancyStart) {
    const isPostReform = parseISO(tenancyStart) >= parseISO(REGIME_START);
    const cycleEnd = toISO(addMonthsISO(tenancyStart, TENANCY_CYCLE_YEARS * 12));
    tenancyRegime = {
      isPostReform,
      cycleEndDate: isPostReform ? cycleEnd : null,
      summary: isPostReform
        ? `This tenancy began on or after 1 March 2026, so it is a Tenancy of Minimum Duration: `
          + `a rolling ${TENANCY_CYCLE_YEARS}-year cycle. Rent is capped at 2% or CPI (whichever is lower) `
          + `during the cycle, and may only be reset to market rent at the end of the cycle `
          + `(${formatDateIE(cycleEnd)}) — 3 years for student-specific accommodation.`
        : `This tenancy began before 1 March 2026. The same 2%-or-CPI cap applies to rent reviews, `
          + `but the rent cannot be reset to market rate while the tenancy continues. `
          + `Pre-reform security-of-tenure and termination rules also continue to apply.`,
    };
  }

  return {
    ok: true,
    errors,
    warnings,
    // money
    currentRent: roundCents(rent),
    maxRent,
    increaseAmount,
    increasePercent,
    annualCapPercent: annual.percent,
    capBasis: annual.basis,
    capExplanation: annual.explanation,
    cpiPercent: Number(cpiPercent),
    annualExtra: roundCents(increaseAmount * 12),
    // timing
    monthsSinceLastSet: months,
    isProRated: months > REVIEW_INTERVAL_MONTHS,
    intervalMonths,
    reviewAllowed,
    earliestReviewDate: earliest,
    earliestEffectiveDate,
    latestServeDate,
    serveDate,
    datesAchievable,
    recommendedEffectiveDate,
    noticeIsPossible,
    noticeDaysAvailable,
    effectiveIfServedToday,
    rtbCopyDeadline,
    // regime
    isNewBuildExempt,
    recentRpzArea,
    tenancyRegime,
  };
}

/**
 * What a valid Notice of Rent Review must contain.
 * Derived from the RTB's rent-review notice guide and prescribed form
 * (checked 2026-09-16) — https://rtb.ie/renting/guide-to-rent-review-notices/
 * The RTB does not publish a field-by-field statutory list on that page,
 * so this is the practical checklist and the UI must say "use the RTB's
 * prescribed form" rather than implying this list is exhaustive.
 */
export const NOTICE_CHECKLIST = [
  'The RTB’s prescribed Notice of Rent Review form (a letter of your own is not valid)',
  'The address of the rented dwelling',
  'The amount of the current rent',
  'The amount of the new rent',
  'The date the new rent takes effect — at least 90 days after service',
  'The date the notice is served, and the landlord’s signature',
  'A statement of how the new rent was calculated (the 2%-or-CPI cap, or the exemption relied on)',
  'Supporting evidence — e.g. your RTB Rent Calculator result and/or RTB Rent Register comparators',
  'A statement of the tenant’s right to dispute the review with the RTB',
  'A copy served on the RTB within 7 days of serving the tenant — without this the notice is invalid',
];
